import { useState, useEffect } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useProfile } from "@/hooks/useProfile";
import { useLeads } from "@/hooks/useLeads";
import { useTeamRole } from "@/hooks/useTeamRole";
import { supabase } from "@/integrations/supabase/client";
import { can, type PermissionKey } from "@/lib/permissions";
import { isFeatureEnabled } from "@/lib/feature-flags";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import {
  LayoutDashboard, Users, Search, Send, SlidersHorizontal, MessageSquare,
  Settings, LogOut, Zap, Bell, AlertTriangle, ShieldCheck, Plus, X, Flame,
  BarChart3, FileText, ClipboardPlus, Shield as ShieldIcon, BellOff,
  CheckSquare, User as UserIcon, UserCog, Mail,
  TrendingUp, Eye, Star, Palette, Trophy, MapPin, CalendarDays,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

type NavItem = { to: string; label: string; icon: any; end?: boolean; perm?: PermissionKey; section?: string };

const getNavItems = (seg: string, role: string | null): NavItem[] => {
  if (role === "staff") {
    return [
      { to: "/tasks", label: "My Tasks", icon: CheckSquare, end: true },
      { to: "/profile", label: "My Profile", icon: UserIcon },
    ];
  }
  const all: NavItem[] = [
    { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: "/leads", label: seg === "grouphome" ? "Inquiries" : "Leads", icon: Users },
    { to: "/appointments", label: "Appointments", icon: CalendarDays },
    { to: "/analytics-owner", label: "Analytics", icon: BarChart3, perm: "viewAnalytics" },
    { to: "/reports", label: "Reports", icon: FileText },
    { to: "/referrals", label: seg === "grouphome" ? "Find Coordinators" : "Find Referrals", icon: Search },
    { to: "/outreach", label: seg === "grouphome" ? "Coordinator Outreach" : "Referral Outreach", icon: Send, perm: "viewReferralOutreach" },
    { to: "/lead-filters", label: seg === "grouphome" ? "Inquiry Filters" : "Lead Filters", icon: SlidersHorizontal, perm: "configureFilters" },
    { to: "/community", label: "Community", icon: Users },
    { to: "/embed", label: seg === "grouphome" ? "Intake Widget" : "Chatbot Embed", icon: MessageSquare, perm: "viewChatbotEmbed" },
    // Growth Marketing nav — only renders when the feature flag is on. Hiding
    // the section entirely (rather than showing 6 links that all dump into a
    // ComingSoon page) keeps the sidebar focused on what actually works today.
    ...(isFeatureEnabled("growthMarketing")
      ? [
          { to: "/seo", label: "SEO Manager", icon: TrendingUp, perm: "viewGrowthMarketing", section: "Growth Marketing" } as NavItem,
          { to: "/visitors", label: "Visitor Tracking", icon: Eye, perm: "viewGrowthMarketing" } as NavItem,
          { to: "/reviews", label: "Reviews", icon: Star, perm: "viewGrowthMarketing" } as NavItem,
          { to: "/toolkit", label: "Marketing Toolkit", icon: Palette, perm: "viewGrowthMarketing" } as NavItem,
          { to: "/partners", label: "Partner Performance", icon: Trophy, perm: "viewGrowthMarketing" } as NavItem,
          { to: "/gbp", label: "Google Business", icon: MapPin, perm: "viewGrowthMarketing" } as NavItem,
        ]
      : []),
    { to: "/team", label: "My Team", icon: UserCog, perm: "manageTeam" },
    { to: "/profile", label: "My Profile", icon: UserIcon },
    { to: "/settings", label: "Settings", icon: Settings, perm: "viewHipaaSettings" },
  ];
  return all.filter(i => !i.perm || can(role as any, i.perm));
};

const MOBILE_TABS_DEFAULT = ["/", "/leads", "/referrals", "/outreach", "/settings"];
const MOBILE_TABS_STAFF = ["/tasks", "/profile"];

function getPageTitle(pathname: string, seg: string): string {
  const map: Record<string, string> = {
    "/": "Dashboard",
    "/leads": seg === "grouphome" ? "Inquiries" : "Leads",
    "/referrals": seg === "grouphome" ? "Find Support Coordinators" : "Find Referrals",
    "/outreach": seg === "grouphome" ? "Coordinator Outreach" : "Referral Outreach",
    "/lead-filters": seg === "grouphome" ? "Inquiry Filters" : "Lead Filters",
    "/analytics": "Analytics",
    "/reports": "Reports",
    "/community": "Community",
    "/quick-intake": "Quick Intake",
    "/embed": seg === "grouphome" ? "Intake Widget Embed" : "Chatbot Embed",
    "/audit-log": "Audit Log",
    "/settings": "Settings",
    "/seo": "SEO Manager",
    "/visitors": "Visitor Tracking",
    "/reviews": "Reviews & Reputation",
    "/toolkit": "Marketing Toolkit",
    "/partners": "Partner Performance",
    "/gbp": "Google Business Profile",
  };
  return map[pathname] || "Dashboard";
}

export default function AppLayout() {
  const { signOut, user } = useAuth() as any;
  const { profile, loading: profileLoading } = useProfile();
  const { leads } = useLeads();
  const location = useLocation();
  const navigate = useNavigate();
  const [notifOpen, setNotifOpen] = useState(false);
  const [signOutConfirmOpen, setSignOutConfirmOpen] = useState(false);
  // Notification read-state is persisted in `notification_reads` so the bell
  // badge stays accurate across refreshes/devices instead of resetting to
  // "everything unread" on every page load.
  // Keyed `${kind}:${lead_id}` so hot-lead and assignment notifications don't
  // share read-state (marking one read shouldn't silently dismiss the other).
  const [notifRead, setNotifRead] = useState<Set<string>>(new Set());
  const [pendingInvites, setPendingInvites] = useState<{
    id: string; agency_name: string; role: string; invited_by_name: string | null; expires_at: string;
  }[]>([]);
  const [assignedNotifs, setAssignedNotifs] = useState<{
    lead_id: string; assigned_at: string; lead_name: string | null; lead_zip: string | null;
  }[]>([]);

  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("notification_reads")
        .select("lead_id, kind")
        .eq("user_id", user.id);
      if (!cancelled && data) {
        setNotifRead(new Set(data.map((r: any) => `${r.kind}:${r.lead_id}`)));
      }
    })();
    return () => { cancelled = true; };
  }, [user?.id]);

  // Recent leads assigned to me by someone else — self-assignments don't
  // need a notification.
  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("lead_assignments")
        .select("lead_id, assigned_at, assigned_by, leads(name, zip_code)")
        .eq("assigned_to", user.id)
        .order("assigned_at", { ascending: false })
        .limit(20);
      if (!cancelled && data) {
        setAssignedNotifs(
          (data as any[])
            .filter(a => a.assigned_by !== user.id)
            .map(a => ({
              lead_id: a.lead_id,
              assigned_at: a.assigned_at,
              lead_name: a.leads?.name ?? null,
              lead_zip: a.leads?.zip_code ?? null,
            }))
        );
      }
    })();
    return () => { cancelled = true; };
  }, [user?.id]);

  // Informational only — the raw invite token is never stored (only its
  // hash), so this can't offer an "Accept" action here. It just tells the
  // invited user an invite exists and to check their email for the link.
  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    (async () => {
      const { data } = await (supabase as any).rpc("get_my_pending_invites");
      if (!cancelled && data) setPendingInvites(data);
    })();
    return () => { cancelled = true; };
  }, [user?.id]);

  async function markAllRead() {
    if (!user?.id) return;
    const rows = [
      ...hotLeads.map(l => ({ user_id: user.id, lead_id: l.id, kind: "hot_lead" })),
      ...assignedNotifs.map(a => ({ user_id: user.id, lead_id: a.lead_id, kind: "assigned" })),
    ];
    if (rows.length === 0) return;
    setNotifRead(prev => new Set([...prev, ...rows.map(r => `${r.kind}:${r.lead_id}`)])); // optimistic
    await supabase.from("notification_reads").upsert(rows, { onConflict: "user_id,lead_id,kind" });
  }

  const { teamRole } = useTeamRole();
  const seg = (profile as any)?.segment || "homecare";
  const navItems = getNavItems(seg, teamRole);
  const MOBILE_TABS = teamRole === "staff" ? MOBILE_TABS_STAFF : MOBILE_TABS_DEFAULT;

  // Inject Inter font
  useEffect(() => {
    if (!document.querySelector('link[href*="fonts.googleapis.com/css2?family=Inter"]')) {
      const link = document.createElement("link");
      link.href = "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap";
      link.rel = "stylesheet";
      document.head.appendChild(link);
    }
    document.body.style.fontFamily = "'Inter', system-ui, sans-serif";
  }, []);

  const hotLeads = leads.filter(l => l.score >= 85);
  const unreadAlerts = hotLeads.filter(l => !notifRead.has(`hot_lead:${l.id}`));
  const unreadAssigned = assignedNotifs.filter(a => !notifRead.has(`assigned:${a.lead_id}`));

  const hipaaOk = !!(profile as any)?.hipaa_acknowledged;

  const initials = ((profile?.owner_name || "U")[0] || "U").toUpperCase();

  return (
    <TooltipProvider delayDuration={150}>
    <div className="min-h-screen flex w-full bg-background" style={{ fontFamily: "'Inter', system-ui, sans-serif" }}>
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex w-60 flex-col border-r border-sidebar-border bg-sidebar fixed inset-y-0 left-0 z-30">
        {/* Brand */}
        <div className="px-5 py-5 border-b border-sidebar-border">
          <NavLink to="/" end className="flex items-center gap-2 hover:opacity-80 transition-opacity">
            <Zap className="h-5 w-5 text-gold" />
            <span className="font-bold text-navy text-[17px]">LegacyGrowth Engine</span>
          </NavLink>
          {profileLoading ? (
            <div className="mt-2 h-5 w-20 rounded-full bg-sidebar-accent animate-pulse" />
          ) : (
            <Badge className={`mt-2 text-xs ${seg === "grouphome" ? "bg-navy text-navy-foreground" : "bg-gold text-gold-foreground"}`}>
              {seg === "grouphome" ? "Group Home" : "Home Care"}
            </Badge>
          )}
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto px-3 py-3 space-y-1">
          {navItems.map(n => (
            <div key={n.to}>
              {n.section && (
                <div className="px-3 pt-4 pb-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
                  {n.section}
                </div>
              )}
              <NavLink
                to={n.to}
                end={n.end}
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-gold-tint text-navy border-l-[3px] border-gold -ml-px"
                      : "text-muted-foreground hover:bg-muted hover:text-foreground"
                  }`
                }
              >
                <n.icon className="h-[18px] w-[18px]" />
                <span>{n.label}</span>
              </NavLink>
            </div>
          ))}
        </nav>

        {/* User */}
        <div className="px-4 py-4 border-t border-sidebar-border">
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 rounded-full bg-navy flex items-center justify-center text-xs font-bold text-gold">
              {initials}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-semibold text-navy truncate">{profile?.owner_name || "User"}</div>
              <div className="text-[12px] text-muted-foreground truncate">{profile?.agency_name || ""}</div>
            </div>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={() => setSignOutConfirmOpen(true)}
                  aria-label="Sign out"
                  className="text-muted-foreground hover:text-foreground"
                >
                  <LogOut className="h-4 w-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent>Sign out</TooltipContent>
            </Tooltip>
          </div>
        </div>
      </aside>

      <AlertDialog open={signOutConfirmOpen} onOpenChange={setSignOutConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure you want to log out?</AlertDialogTitle>
            <AlertDialogDescription>
              You'll need to sign in again to access your dashboard.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => signOut()}>Log Out</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Main */}
      <div className="flex-1 md:ml-60 flex flex-col min-h-screen min-w-0">
        {/* Header */}
        <header className="sticky top-0 z-20 h-16 border-b border-sidebar-border bg-card flex items-center justify-between px-4 sm:px-6">
          <h1 className="text-xl font-bold text-navy">{getPageTitle(location.pathname, seg)}</h1>
          <div className="flex items-center gap-3">
            {/* HIPAA pill */}
            {profileLoading ? (
              <div className="hidden sm:block h-6 w-32 rounded-full bg-muted animate-pulse" />
            ) : hipaaOk ? (
              <button
                type="button"
                onClick={() => navigate("/settings")}
                aria-label="View HIPAA BAA details"
                className="hidden sm:flex items-center gap-1.5 rounded-full bg-success/10 px-3 py-1 text-xs font-medium text-success hover:bg-success/15 transition-colors"
              >
                <ShieldCheck className="h-3.5 w-3.5" /> HIPAA BAA Active
              </button>
            ) : (
              <button
                type="button"
                onClick={() => navigate("/settings")}
                aria-label="Sign your Business Associate Agreement"
                className="hidden sm:flex items-center gap-1.5 rounded-full bg-warning/10 px-3 py-1 text-xs font-medium text-warning hover:bg-warning/15 transition-colors"
              >
                <AlertTriangle className="h-3.5 w-3.5" /> BAA Not Signed
              </button>
            )}

            {/* Bell */}
            <button onClick={() => setNotifOpen(!notifOpen)} className="relative p-2 rounded-lg hover:bg-muted text-muted-foreground">
              <Bell className="h-5 w-5" />
              {(unreadAlerts.length + pendingInvites.length + unreadAssigned.length) > 0 && (
                <span className="absolute -top-0.5 -right-0.5 h-4 w-4 rounded-full bg-hot text-[10px] font-bold text-hot-foreground flex items-center justify-center">
                  {unreadAlerts.length + pendingInvites.length + unreadAssigned.length}
                </span>
              )}
            </button>

            {/* Add lead button — opens Quick Intake form */}
            <Button
              size="sm"
              onClick={() => navigate("/quick-intake")}
              className="hidden sm:flex gap-1.5 bg-gold text-gold-foreground hover:bg-gold/90"
            >
              <Plus className="h-3.5 w-3.5" />
              {seg === "grouphome" ? "Manual Inquiry" : "Manual Lead"}
            </Button>
          </div>
        </header>

        {/* Notification panel */}
        {notifOpen && (
          <>
            <div className="fixed inset-0 z-30 bg-black/20" onClick={() => setNotifOpen(false)} />
            <div className="fixed right-0 top-0 z-40 h-full w-80 bg-card border-l border-sidebar-border shadow-xl flex flex-col animate-in slide-in-from-right duration-200">
              <div className="flex items-center justify-between px-4 py-4 border-b">
                <h2 className="font-bold text-navy text-lg">Notifications</h2>
                <div className="flex items-center gap-2">
                  <button
                    onClick={markAllRead}
                    className="text-xs text-gold font-medium hover:underline"
                  >
                    Mark All Read
                  </button>
                  <button onClick={() => setNotifOpen(false)} className="text-muted-foreground hover:text-foreground">
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto">
                {pendingInvites.map(inv => (
                  <div key={inv.id} className="px-4 py-3 border-b flex gap-3 bg-primary/5">
                    <div className="w-1 rounded-full shrink-0 bg-primary" />
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-navy flex items-center gap-1.5">
                        <Mail className="h-3.5 w-3.5 shrink-0" /> Team Invitation
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5">
                        {inv.invited_by_name || "Someone"} invited you to join {inv.agency_name} as {inv.role}.
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        Check your email for the invite link to accept — expires {new Date(inv.expires_at).toLocaleDateString()}.
                      </div>
                    </div>
                  </div>
                ))}
                {assignedNotifs.map(a => (
                  <div
                    key={a.lead_id}
                    className={`px-4 py-3 border-b flex gap-3 ${
                      !notifRead.has(`assigned:${a.lead_id}`) ? "bg-warning/5" : ""
                    }`}
                  >
                    <div className="w-1 rounded-full shrink-0 bg-primary" />
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-navy flex items-center gap-1.5">
                        <UserCog className="h-3.5 w-3.5 shrink-0" /> Lead Assigned to You
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5">
                        {a.lead_name || "A lead"} in {a.lead_zip || "—"}
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        {new Date(a.assigned_at).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                ))}
                {hotLeads.length === 0 && pendingInvites.length === 0 && assignedNotifs.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
                    <BellOff className="h-8 w-8 mb-2" />
                    <p className="text-sm">No notifications yet.</p>
                  </div>
                ) : (
                  hotLeads.map(l => (
                    <div
                      key={l.id}
                      className={`px-4 py-3 border-b flex gap-3 ${
                        !notifRead.has(`hot_lead:${l.id}`) ? "bg-warning/5" : ""
                      }`}
                    >
                      <div className={`w-1 rounded-full shrink-0 ${l.score === 100 ? "bg-gold" : "bg-hot"}`} />
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-navy">
                          {l.score === 100 ? "Emergency Lead — Score 100" : `Hot Lead — Score ${l.score}`}
                        </div>
                        <div className="text-xs text-muted-foreground mt-0.5">
                          {l.name} in {l.zip_code || "—"}{l.acuity_notes ? ` · ${l.acuity_notes}` : ""}
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">
                          {new Date(l.created_at).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </>
        )}

        {/* Page content */}
        {/* Bottom padding clears the fixed mobile tab bar (h-14) + chat widget
            FAB (bottom-16 to bottom-16+h-14) + "Add lead" FAB (bottom-32 to
            bottom-32+h-14) stacked at the bottom-right on every page, and the
            chat widget FAB on desktop (bottom-6 to bottom-6+h-14). Set once
            here rather than per-page so no route can forget it. */}
        <main className="flex-1 px-4 py-6 sm:px-6 min-w-0 pb-48 md:pb-24">
          <Outlet />
        </main>
      </div>

      {/* Mobile Bottom Tab Bar */}
      {/* Mobile FAB — quick "Add lead" button so mobile users don't have to
          dig into the header to capture a phone-call inquiry. Hidden for
          staff (they don't have permission to create leads). */}
      {teamRole !== "staff" && (
        <button
          onClick={() => navigate("/quick-intake")}
          aria-label="Add lead"
          className="md:hidden fixed bottom-32 right-4 z-30 h-14 w-14 rounded-full bg-gold text-gold-foreground shadow-lg flex items-center justify-center hover:bg-gold/90 active:scale-95 transition-all"
        >
          <Plus className="h-6 w-6" />
        </button>
      )}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-20 bg-card border-t border-sidebar-border flex items-center justify-around h-14">
        {MOBILE_TABS.map(path => {
          const item = navItems.find(n => n.to === path);
          if (!item) return null;
          const isActive = path === "/" ? location.pathname === "/" : location.pathname.startsWith(path);
          return (
            <NavLink key={path} to={path} end={path === "/"} className="flex flex-col items-center gap-0.5 py-1">
              <item.icon className={`h-5 w-5 ${isActive ? "text-gold" : "text-muted-foreground"}`} />
              {isActive && <div className="h-1 w-1 rounded-full bg-gold" />}
            </NavLink>
          );
        })}
      </nav>
    </div>
    </TooltipProvider>
  );
}
